import { useEffect, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Space, Table, Tooltip } from 'antd'
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, StarFilled, StarOutlined } from '@ant-design/icons'
import type { AgentBuilderDashboardView } from '../types'
import { MAX_SAVED_VIEWS_PER_DASHBOARD, MAX_VIEW_NAME_LENGTH } from '../viewer/savedViews'

type Props = {
  open: boolean
  dashboardTitle: string
  views: AgentBuilderDashboardView[]
  loading: boolean
  busyViewId: string | null
  onClose: () => void
  onSaveNew: () => void
  /** Rejects with the API message (e.g. a duplicate name), shown under the name. */
  onRename: (view: AgentBuilderDashboardView, name: string) => Promise<void>
  onToggleDefault: (view: AgentBuilderDashboardView) => void
  onDelete: (view: AgentBuilderDashboardView) => void
}

function formatUpdated(value: string | null) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return days === 1 ? 'Yesterday' : `${days} days ago`
  return date.toLocaleDateString()
}

export function ManageViewsModal({
  open,
  dashboardTitle,
  views,
  loading,
  busyViewId,
  onClose,
  onSaveNew,
  onRename,
  onToggleDefault,
  onDelete,
}: Props) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const atLimit = views.length >= MAX_SAVED_VIEWS_PER_DASHBOARD

  useEffect(() => {
    if (!open) setEditingId(null)
  }, [open])

  function startRename(view: AgentBuilderDashboardView) {
    setEditingId(view.viewId)
    setDraftName(view.name)
    setRenameError(null)
  }

  async function saveRename(view: AgentBuilderDashboardView) {
    if (busyViewId === view.viewId) return
    const name = draftName.trim()
    if (!name) {
      setRenameError('Enter a name for this view.')
      return
    }
    if (name === view.name) {
      setEditingId(null)
      return
    }
    try {
      await onRename(view, name)
      setEditingId(null)
    } catch (error) {
      setRenameError(error instanceof Error && error.message ? error.message : "Couldn't rename the view. Try again.")
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title={(
        <span className="flex flex-col">
          <span>Manage views</span>
          <span className="text-[12px] font-normal text-gray-500">
            {dashboardTitle} · {views.length} of {MAX_SAVED_VIEWS_PER_DASHBOARD} views
          </span>
        </span>
      )}
      footer={(
        <div className="flex items-center justify-between gap-3">
          <span className="text-left text-[12px] text-gray-500">The star marks the view that opens automatically for you.</span>
          <Space>
            <Tooltip title={atLimit ? `You can save up to ${MAX_SAVED_VIEWS_PER_DASHBOARD} views per dashboard. Delete one to save another.` : undefined}>
              <span className="inline-block">
                <Button onClick={onSaveNew} disabled={atLimit}>Save current view…</Button>
              </span>
            </Tooltip>
            <Button type="primary" onClick={onClose}>Done</Button>
          </Space>
        </div>
      )}
    >
      <Table<AgentBuilderDashboardView>
        rowKey="viewId"
        size="small"
        pagination={false}
        loading={loading}
        dataSource={views}
        locale={{ emptyText: 'No saved views yet.' }}
        columns={[
          {
            title: 'Name',
            key: 'name',
            render: (_, view) => editingId === view.viewId ? (
              <div className="flex flex-col gap-1">
                <Space.Compact className="w-full">
                  <Input
                    size="small"
                    value={draftName}
                    maxLength={MAX_VIEW_NAME_LENGTH}
                    autoFocus
                    status={renameError ? 'error' : undefined}
                    aria-label="View name"
                    onChange={(event) => {
                      setDraftName(event.target.value)
                      setRenameError(null)
                    }}
                    onPressEnter={() => void saveRename(view)}
                  />
                  <Button size="small" icon={<CheckOutlined />} aria-label="Save name" loading={busyViewId === view.viewId} onClick={() => void saveRename(view)} />
                  <Button size="small" icon={<CloseOutlined />} aria-label="Cancel rename" onClick={() => setEditingId(null)} />
                </Space.Compact>
                {renameError ? <span className="text-[12px] text-red-500">{renameError}</span> : null}
              </div>
            ) : view.name,
          },
          {
            title: 'Default',
            key: 'default',
            width: 80,
            align: 'center',
            render: (_, view) => (
              <Tooltip title={view.isDefault ? 'Stop opening this view automatically' : 'Open this view automatically'}>
                <Button
                  type="text"
                  size="small"
                  className={view.isDefault ? '!text-brand-500' : '!text-gray-300 hover:!text-brand-500'}
                  icon={view.isDefault ? <StarFilled /> : <StarOutlined />}
                  aria-label={view.isDefault ? `Clear ${view.name} as default` : `Make ${view.name} the default`}
                  disabled={busyViewId === view.viewId}
                  onClick={() => onToggleDefault(view)}
                />
              </Tooltip>
            ),
          },
          {
            title: 'Updated',
            key: 'updated',
            width: 120,
            render: (_, view) => <span className="text-gray-500">{formatUpdated(view.updateDate ?? view.insertDate)}</span>,
          },
          {
            title: '',
            key: 'actions',
            width: 96,
            render: (_, view) => (
              <Space>
                <Button size="small" type="text" icon={<EditOutlined />} aria-label={`Rename ${view.name}`} onClick={() => startRename(view)} />
                <Popconfirm
                  title="Delete this view? This can't be undone."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  cancelText="Keep"
                  onConfirm={() => onDelete(view)}
                >
                  <Button size="small" danger type="text" icon={<DeleteOutlined />} aria-label={`Delete ${view.name}`} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  )
}
